import { useState } from "react";
import { X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WriteGuard } from "@/components/billing/write-guard";
import { useMemberTeamActions, useOrganizationTeams } from "@/hooks/queries/use-organization";
import type { OrganizationMember } from "@/lib/api/organization";

/**
 * A member's teams on the organization members screen, with "Add to team"
 * and a remove button per team. Org admins can manage any team in the
 * organization here, including ones they don't belong to.
 */
export function MemberTeams({ member }: { member: OrganizationMember }) {
  const { data: teams } = useOrganizationTeams();
  const { add, remove } = useMemberTeamActions();
  const [error, setError] = useState<string | null>(null);
  // Remount the picker after each pick so it shows its placeholder again.
  const [pickerKey, setPickerKey] = useState(0);

  const current = member.teams ?? [];
  const onTeam = new Set(current.map((t) => t.id));
  const available = (teams ?? []).filter((t) => !onTeam.has(t.id));
  const busy = add.isPending || remove.isPending;
  const onError = (err: Error) => {
    setError(err.message);
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="member-teams">
      {current.length === 0 && <span className="text-xs text-muted-foreground">No teams yet</span>}
      {current.map((team) => (
        <span
          key={team.id}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs"
        >
          {team.name}
          {team.role !== "member" && <span className="text-muted-foreground">· {team.role}</span>}
          <WriteGuard>
            <button
              type="button"
              disabled={busy}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${member.name} from ${team.name}`}
              onClick={() => {
                setError(null);
                remove.mutate({ memberId: member.id, teamId: team.id }, { onError });
              }}
            >
              <X className="h-3 w-3" />
            </button>
          </WriteGuard>
        </span>
      ))}
      {available.length > 0 && (
        <WriteGuard>
          <Select
            key={pickerKey}
            disabled={busy}
            onValueChange={(teamId) => {
              setError(null);
              setPickerKey((k) => k + 1);
              add.mutate({ memberId: member.id, teamId }, { onError });
            }}
          >
            <SelectTrigger
              className="h-7 w-auto gap-1 rounded-full border-dashed px-2.5 text-xs"
              aria-label={`Add ${member.name} to a team`}
            >
              <SelectValue placeholder="+ Add to team" />
            </SelectTrigger>
            <SelectContent>
              {available.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </WriteGuard>
      )}
      {error && (
        <p role="alert" className="w-full text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
